import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Download,
  Edit,
  Filter,
  MoreHorizontal,
  MoreVertical,
  Plus,
  Share2,
  Users,
} from 'lucide-react';

import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown.tsx';
import { Progress } from '../components/ui/progress.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import { SessionEditPanel } from '../components/sessions/SessionEditPanel';
import {
  useGetSession,
  useUpdateSession,
  useDeleteSession,
} from '../api/session/sessionQueries';
import { toast } from 'react-toastify';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../components/ui/avatar.tsx';

export default function SessionDetails() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
  } = useGetSession(sessionId);
  const { mutate: updateSession } = useUpdateSession();
  const { mutate: deleteSession } = useDeleteSession();

  const session = useMemo(() => {
    if (!sessionData) return null;
    return sessionData;
  }, [sessionData]);

  const handleEditSession = () => {
    setIsEditPanelOpen(true);
  };

  const handleDeleteSession = async () => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await deleteSession(sessionId);
      navigate('/sessions');
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  if (sessionLoading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4" />
          <p className="text-muted-foreground">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4 max-w-md">
            <p className="font-semibold mb-2">Error loading session</p>
            <p>{sessionError.message || 'An unknown error occurred'}</p>
          </div>
          <Button onClick={() => navigate('/sessions')} variant="secondary">
            Back to Sessions
          </Button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg mb-4 max-w-md">
            <p className="font-semibold mb-2">Session Not Found</p>
            <p>The requested session could not be found.</p>
          </div>
          <Button onClick={() => navigate('/sessions')} variant="secondary">
            Back to Sessions
          </Button>
        </div>
      </div>
    );
  }

  const startDate = session.startDate
    ? new Date(session.startDate)
    : new Date();
  const endDate = session.endDate ? new Date(session.endDate) : new Date();
  const duration = Math.round(
    (endDate - startDate) / (1000 * 60 * 60 * 24 * 30)
  ); // Months

  return (
    <div className="w-full space-y-6 p-6">
      {/* Back Button and Header Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          className="gap-2 text-muted-foreground"
          onClick={() => navigate('/sessions')}
        >
          <ArrowLeft size={16} />
          Back to Sessions
        </Button>

        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Share2 size={16} />
            <span className="hidden sm:inline">Share</span>
          </Button>

          <Button variant="outline" className="gap-2">
            <Download size={16} />
            <span className="hidden sm:inline">Export</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost">
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleEditSession}>
                <Edit className="mr-2" size={16} />
                Edit Session
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={handleDeleteSession}
              >
                Delete Session
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Session Overview Card */}
      <Card className="overflow-hidden border-none shadow-sm">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 pb-8 text-white">
          <div className="space-y-2">
            <Badge
              variant="outline"
              className="border-purple-300 text-purple-50"
            >
              Academic Session
            </Badge>
            <CardTitle className="text-2xl sm:text-3xl font-bold text-white">
              {session.name}
            </CardTitle>
            <CardDescription className="text-purple-100">
              {format(startDate, 'MMMM yyyy')} - {format(endDate, 'MMMM yyyy')}{' '}
              · {duration} month{duration !== 1 ? 's' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="-mt-6 space-y-6">
          <Tabs
            defaultValue={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid grid-cols-2 sm:w-fit">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="students">Students</TabsTrigger>
            </TabsList>

            {/* Overview Tab Content */}
            <TabsContent value="overview" className="space-y-6 pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Session Information Card */}
                <Card className="lg:col-span-1 border-none shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Session Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center border-b pb-3">
                      <span className="text-muted-foreground">Session ID</span>
                      <span className="font-medium text-purple-600">
                        {session.sessionId}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-3">
                      <span className="text-muted-foreground">Start Date</span>
                      <span className="font-medium">
                        {format(startDate, 'dd MMM, yyyy')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-3">
                      <span className="text-muted-foreground">End Date</span>
                      <span className="font-medium">
                        {format(endDate, 'dd MMM, yyyy')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pb-3">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium">
                        {duration} month{duration !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions Card */}
                <Card className="lg:col-span-2 border-none shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex flex-col items-center justify-center gap-2"
                        onClick={() => navigate(`/departments`)}
                      >
                        <Users className="h-6 w-6" />
                        <div className="text-center">
                          <p className="font-medium">Manage Departments</p>
                          <p className="text-sm text-muted-foreground">
                            View and edit departments
                          </p>
                        </div>
                      </Button>
                      <Button
                        variant="outline"
                        className="h-auto py-4 flex flex-col items-center justify-center gap-2"
                        onClick={() => navigate('/students')}
                      >
                        <Users className="h-6 w-6" />
                        <div className="text-center">
                          <p className="font-medium">Manage Students</p>
                          <p className="text-sm text-muted-foreground">
                            View and manage students
                          </p>
                        </div>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Students Tab Content */}
            <TabsContent value="students" className="space-y-6 pt-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Enrolled Students</h3>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Plus size={16} className="mr-2" />
                  Add Student
                </Button>
              </div>

              <Card className="border-none shadow-sm">
                <CardContent className="p-0">
                  <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">Student List</h3>
                        <Badge variant="outline">
                          {session.enrollments?.length || 0} Total
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Filter size={16} className="mr-2" />
                          Filter
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download size={16} className="mr-2" />
                          Export
                        </Button>
                      </div>
                    </div>
                  </div>

                  {session.enrollments && session.enrollments.length > 0 ? (
                    <div className="divide-y">
                      {session.enrollments.map((enrollment) => (
                        <div
                          key={enrollment.id}
                          className="p-4 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage
                                src={
                                  enrollment.student?.photoUrl ||
                                  '/placeholder.svg'
                                }
                              />
                              <AvatarFallback>
                                {enrollment.student?.firstName?.[0] || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">
                                {enrollment.student?.firstName}{' '}
                                {enrollment.student?.lastName}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                ID: {enrollment.student?.studentId}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                enrollment.status === 'active'
                                  ? 'success'
                                  : 'secondary'
                              }
                            >
                              {enrollment.status || 'Active'}
                            </Badge>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-muted-foreground">
                      <Users size={40} className="mx-auto mb-2 text-gray-300" />
                      <p>No students enrolled in this session yet</p>
                      <p className="text-sm mt-1">
                        Add students to this session to see them here
                      </p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => navigate('/students')}
                      >
                        Go to Students
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <SessionEditPanel
        session={session}
        isOpen={isEditPanelOpen}
        onClose={() => setIsEditPanelOpen(false)}
      />
    </div>
  );
}
